<template>
  <div :class="props.class">
    <div
      class="position-relative main-wrapper"
      :class="`${props.readOnly && 'disabled'} ${props.wrapperClass || ''}`"
    >
      <InputWrapper
        ref="input-container"
        :border="containerBoxBorder"
        :borderRadius="containerBorderRadius"
        class="container-wrapper"
        :active="state.isFocused || props.focused"
        :error="showError ? props.error : ''"
      >
        <div
          class="slot-before d-flex align-items-center justify-content-center"
        >
          <slot name="before" />
        </div>
        <slot name="input">
          <input
            :id="props.id"
            :readonly="props.readOnly"
            ref="input"
            v-on="inputEvents"
            @input="e => handleInput(e)"
            @change="e => handleChange(e)"
            @focus="e => handleFocus(e)"
            @blur="e => handleBlur(e)"
            @click="handleClick"
            class="main-input form-control shadow-none no-autofill border-transparent tf-input"
            :type="props.inputType ?? 'text'"
            :value="props.value"
            :placeholder="state.isFocused ? '' : props.placeholder"
            :style="props.inputStyles"
            :class="showError && error && 'error'"
          />
        </slot>
        <div
          class="slot-after d-flex align-items-center justify-content-center"
        >
          <slot name="after" />
        </div>
      </InputWrapper>
      <label
        v-if="state.isFocused || props.value"
        class="upper-inscription bg-primary-white text-blue-800"
        :style="{ background: props.upperInscriptionBackground }"
      >
        {{ props.label }}
      </label>
      <InputFieldError v-if="showError" :text="error" />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { defineProps, defineEmits, reactive, withDefaults } from 'vue'

  // Components
  import InputWrapper from './InputWrapper.vue'
  import InputFieldError from './InputFieldError.vue'

  interface Props {
    id?: string
    value: string
    label?: string
    readOnly?: boolean
    placeholder?: string
    upperInscriptionBackground?: string
    inputStyles?: { [key: string]: string }
    containerBoxBorder?: string
    containerBorderRadius?: string
    inputType?: string
    focused?: boolean
    error?: string
    showError?: boolean
    class?: string
    wrapperClass?: string
    allowedType?: 'number' | 'text'
    inputEvents?: Record<string, any>
  }
  interface Emits {
    (e: 'change', value: string): void
    (e: 'input', value: string): void
    (e: 'blur'): void
    (e: 'click'): void
  }

  // state
  const state = reactive({
    isFocused: false,
  })

  // props
  const props = withDefaults(defineProps<Props>(), {
    value: '',
    class: 'py-2',
  })

  // emit
  const emit = defineEmits<Emits>()

  // handlers
  // handlers
  const handleInput = (e: Event) => {
    if (props.readOnly) return
    const inputElement = e.target as HTMLInputElement
    let inputValue = inputElement.value

    // Conditionally filter input based on allowedType
    if (props.allowedType === 'number') {
      // Replace non-numeric characters to enforce numeric input
      inputValue = inputValue.replace(/\D/g, '')
      inputElement.value = inputValue // Set the sanitized value back to the input
    }

    emit('input', inputValue)
  }

  const handleChange = (e: Event) => {
    if (props.readOnly) return
    const inputElement = e.target as HTMLInputElement
    const inputValue = inputElement.value
    emit('change', inputValue)
  }
  const handleFocus = (e: Event) => {
    if (props.readOnly) return
    state.isFocused = true
  }
  const handleBlur = (e: Event) => {
    state.isFocused = false
  }

  const handleClick = (): void => {
    emit('click')
  }
</script>

<style scoped>
  .container-wrapper {
    display: flex;
    position: relative;
    transition: box-shadow 100ms ease;
    overflow: hidden;
  }

  input.error {
    border: 1px solid var(--primary-orange);
    box-shadow:
      0px 0px 0px 4px var(--primary-orange),
      0px 0px 0px 2px rgb(0 0 0 / 25%);
  }

  input:focus {
    border: 1px solid var(--primary-blue);
    box-shadow:
      0px 0px 0px 4px var(--blue-100),
      0px 0px 0px 2px rgb(0 0 0 / 25%);
  }

  .no-autofill:-webkit-autofill,
  .no-autofill:-internal-autofill-selected,
  .no-autofill:-webkit-autofill:active,
  .no-autofill:-webkit-autofill:hover,
  .no-autofill:-webkit-autofill:focus {
    box-shadow: none !important;
    -webkit-box-shadow: none !important;
    -webkit-animation-delay: 0s;
    -webkit-animation-name: none;
    -webkit-animation-fill-mode: none;
    background-color: transparent !important;
  }

  .form-control:disabled {
    background-color: transparent !important;
    color: var(--grey-800) !important;
    cursor: not-allowed !important;
  }

  /* remove arrows on number input */
  input::-webkit-outer-spin-button,
  input::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }

  .no-autofill {
    -webkit-box-shadow: 0 0 0px 1000px transparent inset !important;
    box-shadow: 0 0 0px 1000px transparent inset !important;
  }

  input[type='number'] {
    -moz-appearance: textfield;
  }
</style>
